import { isDemoEnvironment } from "@/components/demo-unavailable/demo-environment.ts";
import { InformBox } from "@/components/inform-box/InformBox.tsx";
import { Box, Stack, Tag, Tooltip, TooltipContent, TooltipTrigger } from "@skailer-com/uikit";
import { cloneElement, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

interface DisableableProps {
  disabled?: boolean;
}

export interface DemoUnavailableProps {
  appearance?: "tag" | "notice";
  children?: ReactElement<DisableableProps>;
  className?: string;
  description?: string;
  label?: string;
  showTag?: boolean;
}

export function DemoUnavailable({
  appearance = "tag",
  children,
  className,
  description,
  label,
  showTag = true,
}: DemoUnavailableProps) {
  const { t } = useTranslation();

  if (!isDemoEnvironment()) return children ?? null;

  const unavailableLabel = label ?? t("integrations:demo_unavailable_tag");
  const unavailableDescription = description ?? t("integrations:demo_unavailable_description");

  if (appearance === "notice") {
    return (
      <Box className={className}>
        <InformBox
          title={unavailableLabel}
          description={unavailableDescription}
          emoji="construction"
        />
      </Box>
    );
  }

  const disabledAction = children ? cloneElement(children, { disabled: true }) : null;

  return (
    <Stack direction="horizontal" gap="md" items="center" className={className}>
      {disabledAction && (
        <Tooltip>
          <TooltipTrigger>
            <Box className="grow cursor-not-allowed [&_*]:cursor-not-allowed">{disabledAction}</Box>
          </TooltipTrigger>
          <TooltipContent className="max-w-80">{unavailableDescription}</TooltipContent>
        </Tooltip>
      )}
      {showTag && (
        <Tag variant="warning" appearance="outline" className="whitespace-nowrap shrink-0">
          {unavailableLabel}
        </Tag>
      )}
    </Stack>
  );
}
